import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getCatalogTree } from "../api/services/hierarchyServices";
import Layout from "../layouts/Layout";
import CompanyUserLayout from "../layouts/CompanyUserLayout";
import CompanyAdminLayout from "../layouts/CompanyAdminLayout";
import AdminLayout from "../layouts/AdminLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import RequestScenarioModal from "../components/RequestScenarioModal";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ToastContext";
import AttackBoxLaunchModal from "../components/lab/AttackBoxLaunchModal";
import ChatWidget from "../components/ChatWidget";
import TksaCoverageModal from "../components/lab/TksaCoverageModal";
import MitreMappingTree from "../components/lab/MitreMappingTree";

import {
  deleteScenario,
  listUserScenarios,
  provisionAttackBox,
  provisionScenario,
  requestVpnCertificate,
  deleteAttackBox,
  getProvisioningStatus,
  getLatestProvisioningStatus,
  getScenarioDetails,
  createScenarioThemeRun,
  createScenarioTopologyRun,
  submitScenarioAnswer,
  getScenarioAttemptStatus,
  provisionVmLab,
  getVmLabProvisioningStatus,
  getLatestVmLabStatus,
  getActiveVmLabs,
  stopVmLab,
} from "../api/services/labService";
import {
  Play,
  BookOpen,
  Monitor,
  AlertCircle,
  Download,
  Square,
  ExternalLink,
  ShieldAlert,
  Lock,
  ArrowRight,
  Network,
  FileText,
  Terminal,
  Trophy,
  BarChart3,
  X,
  CornerDownRight,
} from "lucide-react";
import LabQuestionsSection from "../components/lab/LabQuestionsSection";
import LabWalkthroughTab from "../components/lab/LabWalkthroughTab";
import LabRemediationTab from "../components/lab/LabRemediationTab";
import LabTopologyTab from "../components/lab/LabTopologyTab";
import TeamChallengeModal from "../components/lab/TeamChallengeModal";
import PlatformTeamChallengeModal from "../components/lab/PlatformTeamChallengeModal";

import { hasPermission } from "../utils/permissions";

// Keep in sync with LabQuestionsSection's MAX_ATTEMPTS.
const MAX_ATTEMPTS = 3;

function findNode(node, id) {
  if (node.id === id || node.slug === id) return node;
  for (const child of node.children || []) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return null;
}

const ACTIVE_PROVISION_STATES = new Set(["queued", "in_progress"]);
const POLL_INTERVAL_MS = 3000;
const ATTACKBOX_DIRECT_TIMEOUT_MS = 180000;
const ATTACKBOX_PENDING_MESSAGE =
  "Machine running, preparing secure launch link...";
const ATTACKBOX_TIMEOUT_MESSAGE =
  "Secure launch link could not be generated. Verify AttackBox RAM role and credential upload path.";
const ATTACKBOX_UI_PHASE = Object.freeze({
  DISABLED: "disabled",
  IDLE: "idle",
  STARTING: "starting",
  PENDING: "pending",
  READY: "ready",
  ERROR: "error",
});

function parseIsoTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function hasUnexpiredDirectLaunch(result) {
  const urlType = (result?.url_type || "").toLowerCase();
  if (
    urlType !== "direct" ||
    !result?.guacamole_url ||
    !result?.url_expires_at
  ) {
    return false;
  }
  const expiresAt = parseIsoTimestamp(result.url_expires_at);
  return Boolean(expiresAt && expiresAt.getTime() > Date.now());
}

function findParentCategory(node, targetId, parent = null) {
  if (node.id === targetId || node.slug === targetId) {
    return parent && parent.type === "category" ? parent : null;
  }
  for (const child of node.children || []) {
    const found = findParentCategory(child, targetId, node);
    if (found) return found;
  }
  return null;
}

function extractScenarioIdFromUrl(scenarioUrl) {
  if (typeof scenarioUrl !== "string") return null;
  const match = scenarioUrl.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  );
  return match ? match[0] : null;
}

// ─── TKSA helpers (kept local so the banner chart and the existing alignment
// panel can share the same normalization rules). ────────────────────────────
const TKSA_ID_REGEX = /^([TKSA])(\d+)/i;
function cleanTksaId(raw) {
  if (typeof raw !== "string") return raw;
  const m = raw.trim().match(TKSA_ID_REGEX);
  return m ? `${m[1].toUpperCase()}${m[2]}` : raw.trim();
}
function bucketForTksaId(id) {
  const prefix = id?.[0]?.toUpperCase();
  if (prefix === "T") return "Tasks";
  if (prefix === "S") return "Skills";
  if (prefix === "A") return "Abilities";
  if (prefix === "K") return "Knowledge";
  return "Other";
}

// Visual config for the banner chart. Keys here intentionally mirror the
// existing TKSA Alignment panel so the user sees consistent colors.
const TKSA_BUCKET_CONFIG = [
  { key: "Tasks", short: "T", color: "#378ADD", soft: "rgba(55,138,221,0.18)" },
  { key: "Skills", short: "S", color: "#639922", soft: "rgba(99,153,34,0.18)" },
  {
    key: "Abilities",
    short: "A",
    color: "#7F77DD",
    soft: "rgba(127,119,221,0.18)",
  },
  {
    key: "Knowledge",
    short: "K",
    color: "#BA7517",
    soft: "rgba(186,117,23,0.18)",
  },
  {
    key: "Other",
    short: "•",
    color: "#888888",
    soft: "rgba(128,128,128,0.18)",
  },
];

// ─── TKSA Score Chart ───
// Renders inside the "Challenge Completed" banner. Shows a donut summarizing
// earned-vs-total TKSAs across the whole box, plus a per-category legend with
// a tiny segmented progress bar for each bucket.
//
// `buckets` is an array of:
//   { key, short, color, soft, earned, total }
// (only buckets with total > 0 are passed in).
function TksaScoreChart({ buckets, totalEarned, totalAll }) {
  const RADIUS = 42;
  const CIRC = 2 * Math.PI * RADIUS;
  const overallPct = totalAll > 0 ? (totalEarned / totalAll) * 100 : 0;

  // Build donut segments proportional to each bucket's *total* size,
  // colored at full opacity for the "earned" sub-arc and at soft opacity
  // for the "remaining" portion of that bucket.
  let cursor = 0;
  const segments = [];
  buckets.forEach((b) => {
    const bucketFrac = totalAll > 0 ? b.total / totalAll : 0;
    const bucketLen = bucketFrac * CIRC;
    const earnedFrac = b.total > 0 ? b.earned / b.total : 0;
    const earnedLen = bucketLen * earnedFrac;
    const remainingLen = bucketLen - earnedLen;

    if (earnedLen > 0.01) {
      segments.push({
        color: b.color,
        opacity: 1,
        dashArray: `${earnedLen} ${CIRC - earnedLen}`,
        dashOffset: -cursor,
      });
      cursor += earnedLen;
    }
    if (remainingLen > 0.01) {
      segments.push({
        color: b.color,
        opacity: 0.22,
        dashArray: `${remainingLen} ${CIRC - remainingLen}`,
        dashOffset: -cursor,
      });
      cursor += remainingLen;
    }
  });

  return (
    <div
      className="relative mt-4 grid gap-5 sm:grid-cols-[auto_1fr] items-center rounded-md px-4 py-4"
      style={{
        border: "1px solid var(--color-border-medium)",
        // background: "rgba(0, 0, 0, 0.15)",
      }}
    >
      {/* Donut */}
      <div className="relative flex items-center justify-center mx-auto sm:mx-0">
        <svg
          width="120"
          height="120"
          viewBox="0 0 100 100"
          role="img"
          aria-label={`TKSA score: ${totalEarned} of ${totalAll} earned`}
        >
          {/* Track */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke="var(--color-border-light)"
            strokeOpacity="0.25"
            strokeWidth="9"
          />
          {/* Segments — rotated so the arc starts at the top */}
          <g transform="rotate(-90 50 50)">
            {segments.map((seg, i) => (
              <circle
                key={i}
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                stroke={seg.color}
                strokeOpacity={seg.opacity}
                strokeWidth="9"
                strokeLinecap="butt"
                strokeDasharray={seg.dashArray}
                strokeDashoffset={seg.dashOffset}
              />
            ))}
          </g>
          {/* Center text */}
          <text
            x="50"
            y="46"
            textAnchor="middle"
            style={{
              fill: "var(--color-primary)",
              fontSize: "18px",
              fontWeight: 700,
              fontFamily: "'Courier New', monospace",
            }}
          >
            {totalEarned}/{totalAll}
          </text>
          <text
            x="50"
            y="62"
            textAnchor="middle"
            style={{
              fill: "var(--color-text-light-green)",
              fontSize: "8px",
              fontWeight: 600,
              letterSpacing: "0.1em",
            }}
          >
            {Math.round(overallPct)}% TKSAs
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-2 min-w-0">
        <div
          className="text-[10.5px] font-bold uppercase tracking-[0.14em]"
          style={{ color: "var(--color-primary)" }}
        >
          TKSA Coverage
        </div>
        <div className="flex flex-col gap-1.5">
          {buckets.map((b) => {
            const pct = b.total > 0 ? (b.earned / b.total) * 100 : 0;
            return (
              <div key={b.key} className="flex items-center gap-2.5">
                <span
                  className="w-2 h-2 rounded-md shrink-0"
                  style={{ background: b.color }}
                />
                <span
                  className="text-xs font-semibold shrink-0 w-19.5"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  {b.key}
                </span>
                <div
                  className="flex-1 h-1.5 rounded-md overflow-hidden min-w-15"
                  style={{ background: b.soft }}
                >
                  <div
                    className="h-full rounded-md transition-all"
                    style={{
                      width: `${pct}%`,
                      background: b.color,
                    }}
                  />
                </div>
                <span
                  className="text-[11px] font-mono tabular-nums shrink-0"
                  style={{ color: "var(--color-text-light-green)" }}
                >
                  {b.earned}/{b.total}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function LabPage() {
  const { labId } = useParams();
  const location = useLocation();
  const preloadedLabData = location.state;
  const { user } = useAuth();
  const { showToast } = useToast();
  const [treeData, setTreeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAttackBoxLaunchModal, setShowAttackBoxLaunchModal] =
    useState(false);
  const completionToastShownRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    const loadTree = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await getCatalogTree();
        if (isActive) {
          setTreeData(data);
        }
      } catch (error) {
        if (isActive) {
          setLoadError(error?.message || "Failed to load catalog tree.");
          setTreeData(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadTree();
    return () => {
      isActive = false;
    };
  }, [refreshKey]);

  const lab = treeData ? findNode(treeData, labId) : null;
  const safeLab =
    lab ||
    (preloadedLabData && !treeData
      ? { ...preloadedLabData, id: labId }
      : treeData);
  const parentCategory = treeData ? findParentCategory(treeData, labId) : null;
  const isMultiVm = lab?.lab_type === "multi_vm_proxmox";
  if (lab)
    console.debug(
      "[VM-LAB] lab_type:",
      lab.lab_type,
      "isMultiVm:",
      isMultiVm,
      "keys:",
      Object.keys(lab),
    );
  else
    console.debug(
      "[VM-LAB] lab is null, treeData:",
      !!treeData,
      "labId:",
      labId,
      "safeLab:",
      !!safeLab,
      "preloaded:",
      !!preloadedLabData,
    );
  const teamId = user?.team_ids?.[0] ?? user?.team_id ?? null;
  const roles = user?.roles || [];
  const isCompanyUser = roles.includes("company_user");
  const isCompanyAdmin = roles.includes("company_admin");
  const isPlatformAdmin = roles.includes("platform_admin");

  const canControlTeamScenario = hasPermission(user, "team_scenario_control");
  const [showTeamChallengeModal, setShowTeamChallengeModal] = useState(false);

  const [activeTab, setActiveTab] = useState("play");
  const [machineStatus, setMachineStatus] = useState("stopped");
  const [isDownloadingVpn, setIsDownloadingVpn] = useState(false);
  const [isJoiningMachine, setIsJoiningMachine] = useState(false);
  const [isStoppingMachine, setIsStoppingMachine] = useState(false);
  const [isStartingAttackBox, setIsStartingAttackBox] = useState(false);
  const [isScenarioProvisioning, setIsScenarioProvisioning] = useState(false);
  const [isAttackBoxProvisioning, setIsAttackBoxProvisioning] = useState(false);

  const [scenarioRequestId, setScenarioRequestId] = useState(null);
  const [attackBoxRequestId, setAttackBoxRequestId] = useState(null);
  const [attackBoxUrl, setAttackBoxUrl] = useState(null);
  const [attackBoxUrlExpiresAt, setAttackBoxUrlExpiresAt] = useState(null);
  const [attackBoxLastError, setAttackBoxLastError] = useState(null);
  const [privateIp, setPrivateIp] = useState(null);
  const [targetSubnet, setTargetSubnet] = useState(null);
  const [showTopologyModal, setShowTopologyModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const [buttonMode, setButtonMode] = useState("stopped");
  const joinBusy = isJoiningMachine || isScenarioProvisioning;
  const [scenarioDetails, setScenarioDetails] = useState(null);
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  const [hasScenarioResponse, setHasScenarioResponse] = useState(false);
  const chatScenarioId = useMemo(
    () => extractScenarioIdFromUrl(scenarioDetails?.scenario_url),
    [scenarioDetails?.scenario_url],
  );

  // Progress state. Previously hydrated from localStorage; the backend is now
  // the source of truth for answer correctness and attempt counts (set from
  // the submit-answer response). The restore-on-load path will be wired up to
  // the second endpoint when it's available.
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [attemptedQuestions, setAttemptedQuestions] = useState({});
  // Per-question attempt counter, mirrored from the server's `attempts_used`.
  // The lock/hint UI in LabQuestionsSection keys off `attempts[idx]`.
  const [attempts, setAttempts] = useState({});
  // Map of question index -> revealed correct answer (set from submit response)
  const [revealedAnswers, setRevealedAnswers] = useState({});
  const walkthroughRef = useRef(null);
  const secureLaunchToastShownRef = useRef(false);
  const hydrationGenerationRef = useRef(0);
  const scenarioPollGenerationRef = useRef(0);
  const attackBoxPollGenerationRef = useRef(0);
  const attackBoxPendingSinceRef = useRef(null);
  const vmLabSlotIdRef = useRef(null);
  const prevLabIdRef = useRef(null);
  const [frameworkTab, setFrameworkTab] = useState("tksa");
  const [mappingsModalOpen, setMappingsModalOpen] = useState(false);
  const [tksaModalOpen, setTksaModalOpen] = useState(false);

  const currentLabId = lab?.id || null;
  const expectedScenarioResourceKey =
    user?.username && currentLabId ? `${user.username}:${currentLabId}` : null;
  const directAttackBoxReady = Boolean(
    attackBoxUrl &&
    attackBoxUrlExpiresAt &&
    parseIsoTimestamp(attackBoxUrlExpiresAt)?.getTime() > Date.now(),
  );
  const canOpenAttackBox = machineStatus === "running" && directAttackBoxReady;
  const attackBoxUiPhase = useMemo(() => {
    if (machineStatus !== "running") {
      return ATTACKBOX_UI_PHASE.DISABLED;
    }
    if (canOpenAttackBox) {
      return ATTACKBOX_UI_PHASE.READY;
    }
    if (isStartingAttackBox) {
      return ATTACKBOX_UI_PHASE.STARTING;
    }
    if (isAttackBoxProvisioning) {
      return ATTACKBOX_UI_PHASE.PENDING;
    }
    if (attackBoxLastError) {
      return ATTACKBOX_UI_PHASE.ERROR;
    }
    return ATTACKBOX_UI_PHASE.IDLE;
  }, [
    attackBoxLastError,
    canOpenAttackBox,
    isAttackBoxProvisioning,
    isStartingAttackBox,
    machineStatus,
  ]);

  const attackBoxBusy =
    attackBoxUiPhase === ATTACKBOX_UI_PHASE.STARTING ||
    attackBoxUiPhase === ATTACKBOX_UI_PHASE.PENDING;

  const attackBoxButtonLabel = {
    [ATTACKBOX_UI_PHASE.READY]: "Open in Browser",
    [ATTACKBOX_UI_PHASE.STARTING]: "Starting...",
    [ATTACKBOX_UI_PHASE.PENDING]: "Preparing secure link...",
    [ATTACKBOX_UI_PHASE.ERROR]: "Retry Start Attack Box",
    [ATTACKBOX_UI_PHASE.IDLE]: "Start Attack Box",
    [ATTACKBOX_UI_PHASE.DISABLED]: "Start Attack Box",
  }[attackBoxUiPhase];

  const attackBoxInlineMessage = {
    [ATTACKBOX_UI_PHASE.PENDING]: ATTACKBOX_PENDING_MESSAGE,
    [ATTACKBOX_UI_PHASE.ERROR]: attackBoxLastError,
    [ATTACKBOX_UI_PHASE.IDLE]:
      "Start Attack Box to generate a secure browser launch link.",
    [ATTACKBOX_UI_PHASE.DISABLED]:
      "Join this machine first before starting the Attack Box.",
  }[attackBoxUiPhase];

  const clearAttackBoxLaunchState = useCallback(() => {
    setAttackBoxUrl(null);
    setAttackBoxUrlExpiresAt(null);
    setAttackBoxRequestId(null);
    setIsAttackBoxProvisioning(false);
    setAttackBoxLastError(null);
    attackBoxPendingSinceRef.current = null;
    secureLaunchToastShownRef.current = false;
  }, []);

  const handleDownloadPDF = () => {
    if (!walkthroughRef.current) return;
    const content = walkthroughRef.current.innerHTML;
    const title = scenarioDetails?.title || safeLab?.title || "Walkthrough";
    const envNoteHtml = `
      <div class="env-note">
        <div class="env-note__header">
          <span>Environment Note</span>
        </div>
        <p class="env-note__lead">
          The attack box includes the following base OT libraries by default:
        </p>
        <div class="env-note__chips">
          <span class="env-note__chip">pymodbus==3.12.1</span>
          <span class="env-note__chip">cpppo==5.2.5</span>
          <span class="env-note__chip">python-snap7==2.1.0</span>
          <span class="env-note__chip">asyncua==1.1.8</span>
          <span class="env-note__chip">pydnp3-stepfunc==1.6.0.10</span>
        </div>
        <div class="env-note__callout">
          Library versions may vary slightly. Some examples may need minor
          syntax or API adjustments. If you encounter an error, check the
          installed library version on the attack box and adapt the example
          accordingly. All scripts should be run using python3 (e.g., python3
          script.py).
        </div>
      </div>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title>${title} - Walkthrough</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; line-height: 1.6; color: #1a1a1a; padding: 40px 48px; max-width: 860px; margin: 0 auto; }
            h1 { font-size: 22px; font-weight: 700; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid #e5e7eb; color: #111; }
            h2 { font-size: 17px; font-weight: 700; margin-top: 28px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; color: #111; }
            h3 { font-size: 14px; font-weight: 700; margin-top: 18px; margin-bottom: 6px; color: #111; }
            h4 { font-size: 13px; font-weight: 700; margin-top: 12px; margin-bottom: 4px; color: #111; }
            p { margin: 6px 0; color: #374151; }
            strong { font-weight: 600; color: #111; }
            em { color: #4b5563; font-style: italic; }
            blockquote { border-left: 4px solid #16a34a; background: #f0fdf4; padding: 10px 14px; margin: 12px 0; border-radius: 0 6px 6px 0; color: #374151; }
            blockquote p { margin: 3px 0; }
            code { background: #f0fdf4; color: #15803d; padding: 1px 5px; border-radius: 4px; font-size: 11px; font-family: 'Courier New', monospace; }
            pre { background: #1a1a1a; color: #f3f4f6; padding: 14px 16px; border-radius: 8px; overflow-x: auto; margin: 10px 0; border: 1px solid #374151; }
            pre code { background: none; color: inherit; padding: 0; font-size: 11px; }
            ul { list-style: disc; padding-left: 22px; margin: 6px 0; color: #374151; }
            ol { list-style: decimal; padding-left: 22px; margin: 6px 0; color: #374151; }
            li { margin: 2px 0; }
            hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
            thead { background: #f0fdf4; }
            th { border: 1px solid #d1fae5; padding: 8px 10px; text-align: left; font-weight: 600; color: #111; }
            td { border: 1px solid #e5e7eb; padding: 7px 10px; color: #374151; }
            tr:nth-child(even) td { background: #f9fafb; }
            a { color: #15803d; text-decoration: underline; }
            .env-note { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; margin: 12px 0 20px; background: #ffffff; }
            .env-note__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; font-weight: 700; color: #111; }
            .env-note__tag { font-size: 11px; font-weight: 600; border: 1px solid #e5e7eb; border-radius: 999px; padding: 3px 10px; color: #374151; background: #f9fafb; }
            .env-note__lead { margin: 6px 0 10px; color: #374151; }
            .env-note__chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
            .env-note__chip { border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px 10px; font-size: 11px; color: #111; background: #f9fafb; font-family: 'Courier New', monospace; }
            .env-note__callout { border: 1px solid #f59e0b66; background: #fffbeb; color: #92400e; border-radius: 8px; padding: 8px 10px; font-size: 12px; }
            @media print {
              body { padding: 20px; }
              pre { white-space: pre-wrap; word-break: break-all; }
            }
          </style>
        </head>
        <body>
          <h1 style="border-bottom:2px solid #16a34a">${title} — Walkthrough</h1>
          ${envNoteHtml}
          ${content}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // ─── Fetch scenario details ───────────────────────────────────────────────
  useEffect(() => {
    if (!lab?.id) return;
    let isActive = true;

    const loadScenario = async () => {
      try {
        setHasScenarioResponse(false);
        setIsLoadingScenario(true);
        const data = await getScenarioDetails(lab.id);
        if (isActive) {
          setScenarioDetails(data);

          setHasScenarioResponse(true);
        }
      } catch (err) {
        console.error("Failed to load scenario details:", err);
        if (isActive) {
          setHasScenarioResponse(true);
        }
      } finally {
        if (isActive) setIsLoadingScenario(false);
      }
    };

    loadScenario();
    return () => {
      isActive = false;
    };
  }, [lab?.id]);
  // ─── Hydrate question attempt status from backend ────────────────────────
  // Rebuilds `submitted` + `attempts` on load (and on lab switch) so progress
  // persists across reloads. Backend is the source of truth: is_correct →
  // submitted[idx], attempts_used → attempts[idx]. The lock/hint/resolved UI
  // derives from those exactly as it does after a live submission.
  useEffect(() => {
    const scenarioId = lab?.id || labId;
    if (!scenarioId || !user?.username) return;

    let isActive = true;

    const hydrateAttemptStatus = async () => {
      try {
        const data = await getScenarioAttemptStatus(scenarioId);
        if (!isActive) return;

        const statusQuestions = data?.questions || [];
        const nextSubmitted = {};
        const nextAttempts = {};
        const nextAttempted = {};
        const nextRevealed = {};

        statusQuestions.forEach((q) => {
          const idx = q.index;
          if (typeof idx !== "number") return;

          const attemptsUsed = q.attempts_used ?? 0;
          const isCorrect = Boolean(q.is_correct);

          if (isCorrect) {
            nextSubmitted[idx] = "correct";
            nextAttempted[idx] = true;
          } else if (attemptsUsed > 0) {
            // Wrong submissions on record (locked or not) → show as incorrect.
            nextSubmitted[idx] = "incorrect";
            nextAttempted[idx] = true;
          }
          // attempts_used is 0 for solved questions in this API, so don't let
          // it clobber the "correct" state — only carry a positive count.
          if (attemptsUsed > 0) {
            nextAttempts[idx] = attemptsUsed;
          }
          // If the status response includes a revealed correct answer, store it
          // so the UI can show it when locked. Backend returns `correct_answer`.
          if (q.correct_answer) {
            nextRevealed[idx] = q.correct_answer;
          }
        });

        setSubmitted(nextSubmitted);
        setAttempts(nextAttempts);
        setAttemptedQuestions(nextAttempted);
        setRevealedAnswers(nextRevealed);
        // Populate the input `answers` with any revealed answers so the field
        // shows the correct value after a refresh or when returning to the page.
        if (Object.keys(nextRevealed).length > 0) {
          setAnswers((prev) => ({ ...prev, ...nextRevealed }));
        }
      } catch (error) {
        // 404 = no attempts yet for this scenario; that's a clean slate, not
        // an error worth surfacing.
        if (error?.status !== 404) {
          console.warn("Failed to load scenario attempt status:", error);
        }
      }
    };

    hydrateAttemptStatus();
    return () => {
      isActive = false;
    };
  }, [lab?.id, labId, user?.username]);

  // ─── Hydrate provisioning state ──────────────────────────────────────────
  useEffect(() => {
    if (!user?.username || !currentLabId || !expectedScenarioResourceKey)
      return;

    let active = true;
    const generation = ++hydrationGenerationRef.current;
    let scenarioReadyForCurrentLab = false;
    const isCurrentGeneration = () =>
      active && hydrationGenerationRef.current === generation;
    const showSecureLaunchPendingOnce = () => {
      if (secureLaunchToastShownRef.current) return;
      secureLaunchToastShownRef.current = true;
      showToast(ATTACKBOX_PENDING_MESSAGE, "info");
    };

    const hydrateLatestProvisioning = async () => {
      try {
        const scenarioList = await listUserScenarios({
          username: user.username,
        });
        if (!isCurrentGeneration()) return;

        const runningScenario = (scenarioList?.scenarios || []).find(
          (scenario) => scenario.scenario_id === currentLabId,
        );

        if (runningScenario) {
          scenarioReadyForCurrentLab = true;
          setMachineStatus("running");
          setButtonMode("running");
          setPrivateIp(runningScenario.private_ip || null);
          setIsScenarioProvisioning(false);
          setScenarioRequestId(null);
        } else {
          setMachineStatus("stopped");
          setButtonMode("stopped");
          setPrivateIp(null);
          clearAttackBoxLaunchState();
        }
      } catch (error) {
        console.warn("Failed loading live scenario list:", error);
      }

      try {
        const scenarioStatus = await getLatestProvisioningStatus({
          resourceType: "scenario",
          username: user.username,
          resourceKey: expectedScenarioResourceKey,
        });

        if (!isCurrentGeneration()) return;
        if (
          !scenarioReadyForCurrentLab &&
          ACTIVE_PROVISION_STATES.has(scenarioStatus.state)
        ) {
          setScenarioRequestId(scenarioStatus.request_id);
          setIsScenarioProvisioning(true);
          setMachineStatus("provisioning");
          setButtonMode("stopped");
          clearAttackBoxLaunchState();
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.warn(
            "Failed loading latest scenario provisioning status:",
            error,
          );
        }
      }

      try {
        const attackBoxStatus = await getLatestProvisioningStatus({
          resourceType: "attackbox",
          username: user.username,
          resourceKey: user.username,
        });

        if (!isCurrentGeneration()) return;

        if (!scenarioReadyForCurrentLab) {
          clearAttackBoxLaunchState();
          return;
        }

        if (attackBoxStatus.state === "ready") {
          if (hasUnexpiredDirectLaunch(attackBoxStatus.result)) {
            setAttackBoxUrl(attackBoxStatus.result.guacamole_url);
            setAttackBoxUrlExpiresAt(attackBoxStatus.result.url_expires_at);
            setAttackBoxRequestId(null);
            setIsAttackBoxProvisioning(false);
            setAttackBoxLastError(null);
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
          } else {
            setAttackBoxUrl(null);
            setAttackBoxUrlExpiresAt(null);
            setAttackBoxRequestId(attackBoxStatus.request_id);
            setIsAttackBoxProvisioning(true);
            setAttackBoxLastError(null);
            if (!attackBoxPendingSinceRef.current) {
              attackBoxPendingSinceRef.current = Date.now();
            }
            showSecureLaunchPendingOnce();
          }
        } else if (ACTIVE_PROVISION_STATES.has(attackBoxStatus.state)) {
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setAttackBoxRequestId(attackBoxStatus.request_id);
          setIsAttackBoxProvisioning(true);
          setAttackBoxLastError(null);
          if (!attackBoxPendingSinceRef.current) {
            attackBoxPendingSinceRef.current = Date.now();
          }
        } else {
          clearAttackBoxLaunchState();
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.warn(
            "Failed loading latest attackbox provisioning status:",
            error,
          );
        }
      }
    };

    // Multi-VM: check for existing ready/in-progress vm_lab row
    const hydrateMultiVmLab = async () => {
      // Check for active (ready) vm-labs matching THIS scenario
      try {
        const activeLabs = await getActiveVmLabs();
        if (!isCurrentGeneration()) return;

        if (activeLabs && activeLabs.length > 0) {
          const lab = activeLabs.find(
            (l) => l.resource_key === expectedScenarioResourceKey,
          );
          if (lab) {
            setMachineStatus("running");
            setButtonMode("running");
            setPrivateIp(lab.result?.private_ip || lab.private_ip || null);
            setIsScenarioProvisioning(false);
            setScenarioRequestId(null);
            if (lab.result?.guacamole_url) {
              setAttackBoxUrl(lab.result.guacamole_url);
              setAttackBoxUrlExpiresAt(lab.result.url_expires_at || null);
            }
            vmLabSlotIdRef.current =
              lab.result?.slot_id || lab.instance_id || null;
            setTargetSubnet(
              lab.result?.user_subnet ||
                lab.result?.info_json?.networks?.user_subnet ||
                lab.result?.info_json?.networks?.user_vnet ||
                null,
            );
            return;
          }
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.warn("Failed loading active vm-labs:", error);
        }
      }

      // No active lab for this scenario — check if one is currently provisioning
      try {
        const vmLabStatus = await getLatestVmLabStatus({
          username: user.username,
        });
        if (!isCurrentGeneration()) return;

        if (
          ACTIVE_PROVISION_STATES.has(vmLabStatus.state) &&
          vmLabStatus.resource_key === expectedScenarioResourceKey
        ) {
          setScenarioRequestId(vmLabStatus.request_id);
          setIsScenarioProvisioning(true);
          setMachineStatus("provisioning");
          setButtonMode("stopped");
          clearAttackBoxLaunchState();
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.warn("Failed loading latest vm-lab status:", error);
        }
      }
    };

    if (isMultiVm) {
      console.debug("[VM-LAB] Hydrating multi-VM lab state");
      hydrateMultiVmLab();
    } else {
      hydrateLatestProvisioning();
    }

    return () => {
      active = false;
    };
  }, [
    clearAttackBoxLaunchState,
    currentLabId,
    expectedScenarioResourceKey,
    isMultiVm,
    showToast,
    user?.username,
  ]);

  // ─── Poll scenario provisioning ──────────────────────────────────────────
  useEffect(() => {
    if (!scenarioRequestId) return;

    let active = true;
    const generation = ++scenarioPollGenerationRef.current;
    let timerId = null;
    const scheduleNext = () => {
      if (!active || scenarioPollGenerationRef.current !== generation) return;
      timerId = setTimeout(pollScenarioStatus, POLL_INTERVAL_MS);
    };

    const pollScenarioStatus = async () => {
      try {
        const statusFetcher = isMultiVm
          ? getVmLabProvisioningStatus
          : getProvisioningStatus;
        const status = await statusFetcher({
          requestId: scenarioRequestId,
        });
        if (!active || scenarioPollGenerationRef.current !== generation) return;

        if (status.state === "ready") {
          setMachineStatus("running");
          setButtonMode("running");
          setPrivateIp(status.result?.private_ip || status.private_ip || null);
          setIsScenarioProvisioning(false);
          setScenarioRequestId(null);
          showToast("Machine provisioned successfully.", "success");

          // For multi-VM labs, set guac URL directly (no separate attackbox provision)
          if (isMultiVm && status.result) {
            if (status.result.guacamole_url) {
              setAttackBoxUrl(status.result.guacamole_url);
              setAttackBoxUrlExpiresAt(status.result.url_expires_at || null);
            }
            vmLabSlotIdRef.current = status.result.slot_id || null;
            setTargetSubnet(
              status.result.user_subnet ||
                status.result.info_json?.networks?.user_subnet ||
                status.result.info_json?.networks?.user_vnet ||
                null,
            );
          }
          return;
        }

        if (status.state === "failed" || status.state === "cancelled") {
          setMachineStatus("stopped");
          setButtonMode("stopped");
          setPrivateIp(null);
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setAttackBoxRequestId(null);
          setIsAttackBoxProvisioning(false);
          setIsScenarioProvisioning(false);
          setScenarioRequestId(null);
          showToast(status.error_message || "Failed to join machine.", "error");
          return;
        }

        if (ACTIVE_PROVISION_STATES.has(status.state)) {
          setMachineStatus("provisioning");
          setButtonMode("stopped");
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setIsScenarioProvisioning(true);
          scheduleNext();
          return;
        }
      } catch (error) {
        if (!active || scenarioPollGenerationRef.current !== generation) return;
        if (error?.status === 404) {
          setIsScenarioProvisioning(false);
          setScenarioRequestId(null);
          return;
        }
        showToast(
          error.message || "Failed to refresh machine status.",
          "error",
        );
        scheduleNext();
        return;
      }
      scheduleNext();
    };

    pollScenarioStatus();

    return () => {
      active = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [scenarioRequestId, isMultiVm, showToast]);

  // ─── Poll attack box provisioning ────────────────────────────────────────
  useEffect(() => {
    if (!attackBoxRequestId) return;

    let active = true;
    const generation = ++attackBoxPollGenerationRef.current;
    let timerId = null;
    const scheduleNext = () => {
      if (!active || attackBoxPollGenerationRef.current !== generation) return;
      timerId = setTimeout(pollAttackBoxStatus, POLL_INTERVAL_MS);
    };
    if (!attackBoxPendingSinceRef.current) {
      attackBoxPendingSinceRef.current = Date.now();
    }

    const pollAttackBoxStatus = async () => {
      try {
        const status = await getProvisioningStatus({
          requestId: attackBoxRequestId,
        });
        if (!active || attackBoxPollGenerationRef.current !== generation)
          return;

        const timedOut =
          Date.now() - (attackBoxPendingSinceRef.current || Date.now()) >
          ATTACKBOX_DIRECT_TIMEOUT_MS;

        if (status.state === "ready") {
          if (hasUnexpiredDirectLaunch(status.result)) {
            setAttackBoxUrl(status.result.guacamole_url);
            setAttackBoxUrlExpiresAt(status.result.url_expires_at);
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(null);
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast("Attack box provisioned successfully.", "success");
            return;
          }

          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setIsAttackBoxProvisioning(true);
          if (timedOut) {
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(
              status.error_message || ATTACKBOX_TIMEOUT_MESSAGE,
            );
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast(
              status.error_message || ATTACKBOX_TIMEOUT_MESSAGE,
              "error",
            );
            return;
          }
          if (!secureLaunchToastShownRef.current) {
            secureLaunchToastShownRef.current = true;
            showToast(ATTACKBOX_PENDING_MESSAGE, "info");
          }
          scheduleNext();
          return;
        }

        if (status.state === "failed" || status.state === "cancelled") {
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setIsAttackBoxProvisioning(false);
          setAttackBoxRequestId(null);
          setAttackBoxLastError(
            status.error_message ||
              "Failed to initialize secure launch link. Verify AttackBox RAM role and credential upload.",
          );
          attackBoxPendingSinceRef.current = null;
          secureLaunchToastShownRef.current = false;
          showToast(
            status.error_message ||
              "Failed to initialize secure launch link. Verify AttackBox RAM role and credential upload.",
            "error",
          );
          return;
        }

        if (ACTIVE_PROVISION_STATES.has(status.state)) {
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setIsAttackBoxProvisioning(true);
          if (timedOut) {
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(ATTACKBOX_TIMEOUT_MESSAGE);
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast(ATTACKBOX_TIMEOUT_MESSAGE, "error");
            return;
          }
          scheduleNext();
          return;
        }
      } catch (error) {
        if (!active || attackBoxPollGenerationRef.current !== generation)
          return;
        if (error?.status === 404) {
          setIsAttackBoxProvisioning(false);
          setAttackBoxRequestId(null);
          setAttackBoxUrl(null);
          setAttackBoxUrlExpiresAt(null);
          setAttackBoxLastError(null);
          attackBoxPendingSinceRef.current = null;
          return;
        }
        setAttackBoxLastError(
          error.message || "Failed to refresh attack box status.",
        );
        showToast(
          error.message || "Failed to refresh attack box status.",
          "error",
        );
        scheduleNext();
        return;
      }
      scheduleNext();
    };

    pollAttackBoxStatus();

    return () => {
      active = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [attackBoxRequestId, showToast]);

  const questions = scenarioDetails?.metadata?.questions || [];
  const runbookMarkdown = scenarioDetails?.metadata?.runbook || "";
  const playbookMarkdown = scenarioDetails?.metadata?.playbook || "";
  const tksaBuckets = useMemo(() => {
    const sets = {
      Tasks: new Set(),
      Skills: new Set(),
      Abilities: new Set(),
      Knowledge: new Set(),
      Other: new Set(),
    };

    questions.forEach((q) => {
      (q.framework_alignment?.tksa_ids || []).forEach((raw) => {
        const id = cleanTksaId(raw);
        sets[bucketForTksaId(id)].add(id);
      });
    });

    return Object.entries(sets)
      .map(([label, set]) => ({ label, items: [...set].sort() }))
      .filter((b) => b.items.length > 0);
  }, [questions]);

  const walkthroughMarkdown =
    scenarioDetails?.metadata?.walkthrough_markdown || "";
  const mitreAttack = scenarioDetails?.metadata?.mitre_attack || null;
  const hasTksaData = tksaBuckets.length > 0;
  const hasMitreData =
    !!mitreAttack &&
    Object.values(mitreAttack).some((v) => Array.isArray(v) && v.length > 0);
  // Fall back to whichever tab actually has data so the card never renders empty.
  const activeFrameworkTab =
    frameworkTab === "mitre"
      ? hasMitreData
        ? "mitre"
        : "tksa"
      : hasTksaData
        ? "tksa"
        : "mitre";
  const totalQuestions = questions.length;
  const showQuestionsLoader =
    isLoadingScenario || (!hasScenarioResponse && !loadError);
  const allQuestionsAttempted =
    totalQuestions > 0 &&
    Object.keys(attemptedQuestions).length >= totalQuestions;

  // A question is "resolved" if the user got it right OR burned all attempts.
  // The Challenge Completed banner now fires on resolution, not just on
  // correctness, so a user who exhausts attempts still sees their score.
  const allQuestionsResolved =
    totalQuestions > 0 &&
    questions.every(
      (_q, idx) =>
        submitted[idx] === "correct" || (attempts[idx] ?? 0) >= MAX_ATTEMPTS,
    );

  const allQuestionsCorrect =
    totalQuestions > 0 &&
    questions.every((_q, idx) => submitted[idx] === "correct");

  // ─── TKSA scoring for the completion banner ──────────────────────────────
  // `score` totals = unique TKSA IDs across ALL questions (per bucket).
  // `score` earned = unique TKSA IDs across CORRECT questions (per bucket).
  // Counting unique IDs (not occurrences) means answering two questions that
  // share a TKSA doesn't double-count it — which matches the existing TKSA
  // Alignment panel above.
  const tksaScore = useMemo(() => {
    const totals = {
      Tasks: new Set(),
      Skills: new Set(),
      Abilities: new Set(),
      Knowledge: new Set(),
      Other: new Set(),
    };
    const earned = {
      Tasks: new Set(),
      Skills: new Set(),
      Abilities: new Set(),
      Knowledge: new Set(),
      Other: new Set(),
    };

    questions.forEach((q, idx) => {
      const ids = (q.framework_alignment?.tksa_ids || []).map(cleanTksaId);
      const isCorrect = submitted[idx] === "correct";
      ids.forEach((id) => {
        const bucket = bucketForTksaId(id);
        totals[bucket].add(id);
        if (isCorrect) earned[bucket].add(id);
      });
    });

    const buckets = TKSA_BUCKET_CONFIG.map((cfg) => ({
      ...cfg,
      earned: earned[cfg.key].size,
      total: totals[cfg.key].size,
    })).filter((b) => b.total > 0);

    const totalEarned = buckets.reduce((s, b) => s + b.earned, 0);
    const totalAll = buckets.reduce((s, b) => s + b.total, 0);

    return { buckets, totalEarned, totalAll };
  }, [questions, submitted]);

  // ─── Per-TKSA detail for the coverage modal ──────────────────────────────
  // Unlike `tksaScore` (which only keeps unique-ID sets), this also records
  // WHICH questions carry each TKSA and how each resolved — so the modal can
  // show that a shared tag like T1250 was correct on Q1 but wrong on Q3, and
  // score it proportionally (1/2 attempts → 50%) instead of snapping to full.
  const BUCKET_SINGULAR = {
    Tasks: "Task",
    Skills: "Skill",
    Abilities: "Ability",
    Knowledge: "Knowledge area",
    Other: "item",
  };

  const tksaCoverageDetail = useMemo(() => {
    // id -> { bucket, questions:[{index,status}], attemptsCorrect, attemptsTotal }
    const byId = new Map();

    questions.forEach((q, idx) => {
      const ids = (q.framework_alignment?.tksa_ids || []).map(cleanTksaId);
      // A question's status: correct, resolved-but-wrong, or still pending.
      let status;
      if (submitted[idx] === "correct") status = "correct";
      else if (
        (attempts[idx] ?? 0) >= MAX_ATTEMPTS ||
        submitted[idx] === "incorrect"
      )
        status = "incorrect";
      else status = "pending";

      ids.forEach((rawId) => {
        const id = rawId;
        if (!byId.has(id)) {
          byId.set(id, {
            id,
            bucket: bucketForTksaId(id),
            questions: [],
            attemptsCorrect: 0,
            attemptsTotal: 0,
          });
        }
        const rec = byId.get(id);
        rec.questions.push({ index: idx, status });
        // Only resolved questions count toward the attempt ratio; pending ones
        // don't penalize the score yet.
        if (status !== "pending") {
          rec.attemptsTotal += 1;
          if (status === "correct") rec.attemptsCorrect += 1;
        }
      });
    });

    const tksaEntries = [...byId.values()].map((rec) => ({
      ...rec,
      bucketSingular: BUCKET_SINGULAR[rec.bucket] ?? "item",
    }));

    return {
      buckets: tksaScore.buckets,
      totalEarned: tksaScore.totalEarned,
      totalAll: tksaScore.totalAll,
      tksaEntries,
    };
  }, [questions, submitted, attempts, tksaScore]);

  // Counts for the modal's stat strip.
  const questionsCorrectCount = useMemo(
    () => questions.filter((_q, idx) => submitted[idx] === "correct").length,
    [questions, submitted],
  );
  const questionsResolvedCount = useMemo(
    () =>
      questions.filter(
        (_q, idx) =>
          submitted[idx] === "correct" || (attempts[idx] ?? 0) >= MAX_ATTEMPTS,
      ).length,
    [questions, submitted, attempts],
  );

  // ─── Submit a single answer to the backend ───────────────────────────────
  // The backend is the source of truth: it returns is_correct, attempts_used,
  // and the lock state. We mirror those into `submitted` and `attempts`, then
  // hand a small result object back to LabQuestionsSection so it can drive the
  // shake/auto-advance UI.
  const handleSubmitAnswer = async (idx) => {
    const scenarioId = lab?.id || labId;
    if (!scenarioId) {
      showToast("Scenario ID is missing.", "error");
      throw new Error("Missing scenario id");
    }

    const answerText = (answers[idx] ?? "").trim();

    let response;
    try {
      response = await submitScenarioAnswer({
        scenarioId,
        questionIndex: idx,
        answerText,
      });
    } catch (error) {
      showToast(error?.message || "Failed to submit answer.", "error");
      throw error;
    }

    const isCorrect = Boolean(response?.is_correct);
    const attemptsUsed = response?.attempts_used ?? 0;
    const locked = Boolean(response?.locked);
    const lockedReason = response?.locked_reason ?? null;
    const correctAnswer = response?.correct_answer ?? null;

    setSubmitted((prev) => ({
      ...prev,
      [idx]: isCorrect ? "correct" : "incorrect",
    }));
    // Mirror the server's count. The lock/hint UI keys off
    // `attempts[idx] >= MAX_ATTEMPTS`, and the backend returns
    // attempts_used: 3 on a max_attempts lock — so the derived lock state
    // stays correct without a second source of truth.
    setAttempts((prev) => ({ ...prev, [idx]: attemptsUsed }));

    // If the server returned a revealed/correct answer (for example on lock),
    // store it separately so the frontend does not read it from the scenario
    // details payload.
    if (correctAnswer) {
      setRevealedAnswers((prev) => ({ ...prev, [idx]: correctAnswer }));
      // Also populate the answer input so the revealed correct answer appears
      // inside the field immediately after a lock/submission.
      setAnswers((prev) => ({ ...prev, [idx]: correctAnswer }));
    }

    return { isCorrect, locked, lockedReason, attemptsUsed, correctAnswer };
  };

  const handleDownloadVpn = async () => {
    if (!user?.username) {
      showToast("Please log in to request a VPN certificate.", "warning");
      return;
    }

    try {
      setIsDownloadingVpn(true);
      const response = await requestVpnCertificate({
        protocol: "udp",
        username: user.username,
      });

      if (!response?.download_url) {
        showToast("Download URL missing in response.", "error");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = response.download_url;
      anchor.download =
        response.certificate_path?.split("/").pop() || "vpn-config.ovpn";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      showToast("VPN certificate download started.", "success");
    } catch (error) {
      showToast(
        error.message || "Failed to download VPN certificate.",
        "error",
      );
    } finally {
      setIsDownloadingVpn(false);
    }
  };

  const handleJoinMachine = async () => {
    if (!user?.username) {
      showToast("Please log in to join the machine.", "warning");
      return;
    }

    const scenarioId = lab?.id || labId;
    if (!scenarioId) {
      showToast("Scenario ID is missing for this box.", "error");
      return;
    }

    try {
      hydrationGenerationRef.current += 1;
      setIsJoiningMachine(true);
      setMachineStatus("provisioning");
      setButtonMode("stopped");
      setPrivateIp(null);
      clearAttackBoxLaunchState();
      secureLaunchToastShownRef.current = false;
      const response = isMultiVm
        ? await provisionVmLab({ scenarioId, username: user.username })
        : await provisionScenario({ scenarioId, username: user.username });

      if (!response?.request_id) {
        throw new Error("Provisioning request ID missing in response.");
      }

      setScenarioRequestId(response.request_id);
      setIsScenarioProvisioning(true);
      showToast("Machine provisioning started.", "info");
      console.debug("Scenario provisioned:", response);
    } catch (error) {
      showToast(error.message || "Failed to join machine.", "error");
      setMachineStatus("stopped");
      setButtonMode("stopped");
    } finally {
      setIsJoiningMachine(false);
    }
  };

  const handleStopMachine = async () => {
    if (!user?.username) {
      showToast("Please log in to stop the machine.", "warning");
      return;
    }

    const scenarioId = lab?.id || labId;
    if (!scenarioId) {
      showToast("Scenario ID is missing for this box.", "error");
      return;
    }

    try {
      hydrationGenerationRef.current += 1;
      setIsStoppingMachine(true);
      setMachineStatus("provisioning");

      if (isMultiVm) {
        // Multi-VM: teardown via slot_id from the last ready result
        const slotId = vmLabSlotIdRef.current;
        if (slotId) {
          await stopVmLab({ slotId });
        }
      } else {
        await deleteScenario({ username: user.username, scenarioId });
        await deleteAttackBox({ username: user.username });
      }

      showToast("Machine and Attack Box stopped successfully.", "success");
      setMachineStatus("stopped");
      setPrivateIp(null);
      clearAttackBoxLaunchState();
      setButtonMode("stopped");
      setIsScenarioProvisioning(false);
      setScenarioRequestId(null);
      secureLaunchToastShownRef.current = false;
      console.debug("Scenario and AttackBox deleted successfully");
    } catch (error) {
      showToast(error.message || "Failed to stop machine.", "error");
      setMachineStatus("running");
      setButtonMode("running");
    } finally {
      setIsStoppingMachine(false);
    }
  };

  const handleStartAttackBox = async () => {
    if (!user?.username) {
      showToast("Please log in to start the attack box.", "warning");
      return;
    }
    if (machineStatus !== "running") {
      showToast(
        "Join this machine first before starting the attack box.",
        "warning",
      );
      return;
    }

    // Multi-VM: attackbox is already provisioned — just open the guac URL
    if (isMultiVm) {
      if (attackBoxUrl) {
        window.open(attackBoxUrl, "_blank");
      } else {
        showToast("Attack box URL not available yet.", "warning");
      }
      return;
    }

    try {
      hydrationGenerationRef.current += 1;
      setIsStartingAttackBox(true);
      setAttackBoxUrl(null);
      setAttackBoxUrlExpiresAt(null);
      setAttackBoxLastError(null);
      attackBoxPendingSinceRef.current = Date.now();
      secureLaunchToastShownRef.current = false;
      const response = await provisionAttackBox({
        autoTerminateHours: 4,
        instanceType: "ecs.t6-c1m2.large",
        username: user.username,
        scenarioId: chatScenarioId,
      });

      if (!response?.request_id) {
        throw new Error("Provisioning request ID missing in response.");
      }

      setAttackBoxRequestId(response.request_id);
      setIsAttackBoxProvisioning(true);
      showToast(ATTACKBOX_PENDING_MESSAGE, "info");
      console.debug("AttackBox provisioned:", response);
    } catch (error) {
      setAttackBoxLastError(error.message || "Failed to start attack box.");
      attackBoxPendingSinceRef.current = null;
      showToast(error.message || "Failed to start attack box.", "error");
    } finally {
      setIsStartingAttackBox(false);
    }
  };

  const handleOpenAttackBox = () => {
    if (!canOpenAttackBox) {
      showToast("Secure Attack Box launch link is not ready yet.", "warning");
      return;
    }
    if (!attackBoxUrl) {
      showToast("Attack box URL not available.", "error");
      return;
    }
    setShowAttackBoxLaunchModal(true);
  };

  const handleLaunchAttackBoxUrl = () => {
    if (!attackBoxUrl) {
      showToast("Attack box URL not available.", "error");
      return;
    }
    window.open(attackBoxUrl, "_blank");
  };

  const handleSubmitScenarioRequest = async (payload) => {
    try {
      let response;

      if (payload.mode === "topology") {
        const topologyRequest = {
          cve: payload.labCve,
          topology_id: payload.topology_id,
          category_id: payload.categoryId,
          team_id: teamId,
        };
        console.log("[Topology Mode] Request payload:", topologyRequest);
        response = await createScenarioTopologyRun(topologyRequest);
      } else {
        const themeRequest = {
          cve: payload.labCve,
          vuln_theme: payload.notes,
          category_id: payload.categoryId,
          team_id: teamId,
        };
        console.log("[Description Mode] Request payload:", themeRequest);
        response = await createScenarioThemeRun(themeRequest);
      }

      console.log("Flow Run Response:", response);
      console.log("Flow Run ID:", response?.flow_run_id);
      console.log("Status:", response?.status);

      showToast("Box request submitted successfully", "success");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to submit box request:", error);
      const errorMessage =
        error?.message || "Failed to submit box request. Please try again.";
      showToast(errorMessage, "error");
      throw error;
    }
  };

  // The box's saved topology (same source the boxes-page card previews). Only
  // surface the Topology tab when the box actually carries topology data.
  const labTopologyNodes = safeLab?.topology_json?.nodes || [];
  const labTopologyEdges = safeLab?.topology_json?.edges || [];
  const hasLabTopology = labTopologyNodes.length > 0;

  const tabs = [
    { id: "play", label: "Play Machine", icon: Play },
    { id: "walkthroughs", label: "Walkthroughs", icon: BookOpen },
    { id: "remediation", label: "Response and Remediation", icon: FileText },
    ...(hasLabTopology
      ? [{ id: "topology", label: "Topology", icon: Network }]
      : []),
    { id: "info", label: "Request a Box", icon: ShieldAlert },
  ];

  const normalizedRoles = (user?.roles || []).map((role) => role.toLowerCase());
  const isProfessional = normalizedRoles.includes("professional");
  const isStudent = normalizedRoles.includes("student");
  const canRequestScenario = Boolean(user?.permissions?.scenario_creation);

  const PageLayout = isCompanyUser
    ? CompanyUserLayout
    : isCompanyAdmin
      ? CompanyAdminLayout
      : isPlatformAdmin
        ? AdminLayout
        : Layout;
  useEffect(() => {
    completionToastShownRef.current = false;
  }, [labId]);
  useEffect(() => {
    if (!lab?.id) return;
    if (prevLabIdRef.current && prevLabIdRef.current !== lab.id) {
      setAnswers({});
      setSubmitted({});
      setAttemptedQuestions({});
      // Per-question attempt counter is also lab-scoped — clear it when
      // switching boxes so a fresh lab doesn't inherit prior locks.
      setAttempts({});
    }
    prevLabIdRef.current = lab.id;
  }, [lab?.id]);

  useEffect(() => {
    if (!allQuestionsResolved || completionToastShownRef.current) return;
    completionToastShownRef.current = true;
    const message = allQuestionsCorrect
      ? "Challenge completed."
      : "Challenge complete — review your TKSA score.";
    showToast(message, "success", 10000);
  }, [allQuestionsResolved, allQuestionsCorrect, showToast]);
  return (
    <PageLayout>
      <RequestScenarioModal
        open={showTopologyModal}
        onClose={() => setShowTopologyModal(false)}
        onSubmit={handleSubmitScenarioRequest}
        mode="topology"
        prefilledLab={
          safeLab && parentCategory
            ? {
                id: safeLab.id,
                title: safeLab.title,
                cve: safeLab.cve,
                category_id: parentCategory.id,
              }
            : null
        }
      />
      <RequestScenarioModal
        open={showDescriptionModal}
        onClose={() => setShowDescriptionModal(false)}
        onSubmit={handleSubmitScenarioRequest}
        mode="description"
        prefilledLab={
          safeLab && parentCategory
            ? {
                id: safeLab.id,
                title: safeLab.title,
                cve: safeLab.cve,
                category_id: parentCategory.id,
              }
            : null
        }
      />
      <div className="w-full max-w-362.5 mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 min-h-[calc(100vh-4rem)]">
        <div className="space-y-4">
          {isLoading && !treeData ? (
            <div className="h-6 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-64"></div>
          ) : (
            <Breadcrumbs currentNode={safeLab} tree={treeData} />
          )}
          <div className="flex flex-col gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] text-(--color-primary)/80">
                Box
              </p>
              {isLoading && !preloadedLabData ? (
                <div className="space-y-3 mt-1">
                  <div className="h-9 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-96 max-w-full"></div>
                  <div className="h-5 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full max-w-3xl"></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap mt-1">
                    <h1 className="text-2xl sm:text-3xl font-bold text-(--color-text-primary) leading-tight [text-shadow:0_0_12px_var(--color-shadow-primary)]/30">
                      {safeLab?.title ||
                        preloadedLabData?.title ||
                        "Box not found"}
                    </h1>
                    {!isLoadingScenario &&
                      hasScenarioResponse &&
                      (hasTksaData || hasMitreData) && (
                        <button
                          type="button"
                          onClick={() => setMappingsModalOpen(true)}
                          className="shrink-0 inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-(--color-primary)/5 px-4 py-2 text-xs sm:text-sm font-semibold text-(--color-primary) hover:bg-(--color-primary)/10 hover:shadow-[0_0_15px_rgba(0,255,0,0.2)] transition-all"
                        >
                          <BarChart3 className="w-4 h-4" />
                          View TKSA Alignment and MITRE Mapping
                        </button>
                      )}
                    {(isLoadingScenario ||
                      (!hasScenarioResponse && !loadError)) && (
                      <div className="shrink-0 h-9 w-64 max-w-full rounded-md bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 animate-pulse bg-size-[200%_100%]" />
                    )}
                  </div>
                  <p className="text-(--color-text-muted) dark:text-gray-400 text-sm max-w-3xl mt-3">
                    Hands-on exercise environment. Use the details provided to
                    launch the Box environment.
                  </p>
                </>
              )}
            </div>
            {loadError && (
              <div className="text-sm text-red-400">
                {loadError}{" "}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => setRefreshKey((prev) => prev + 1)}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
          {allQuestionsResolved && (
            <div
              className="relative overflow-hidden rounded-[10px]"
              style={{
                background: "transparent",
                backdropFilter: "none",
                border: "1px solid var(--color-border-medium)",
                padding: "18px 22px 18px 24px",
              }}
            >
              {/* scanline texture */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(0,255,0,0.02) 23px, rgba(0,255,0,0.02) 24px)",
                }}
              />
              {/* top edge glow line */}
              <div
                className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, var(--color-border-medium) 20%, var(--color-border-dark) 50%, var(--color-border-medium) 80%, transparent 100%)",
                }}
              />
              {/* left accent bar */}
              <div
                className="absolute left-0 top-[10%] bottom-[10%] w-0.75 rounded-r-sm pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent, var(--color-primary) 30%, var(--color-primary) 70%, transparent)",
                }}
              />
              {/* content */}
              <div className="relative flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3.5">
                  <div className="relative shrink-0 w-11 h-11 rounded-md flex items-center justify-center backdrop-blur-md bg-(--color-bg)">
                    <div
                      className="absolute -inset-1.75 rounded-md animate-ping"
                      style={{
                        border: "1px solid var(--color-border-light)",
                        animationDuration: "2.8s",
                      }}
                    />
                    <Trophy
                      className="w-5 h-5"
                      style={{ color: "var(--color-primary)" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span
                      className="text-sm font-bold uppercase"
                      style={{ color: "var(--color-primary)" }}
                    >
                      Challenge completed
                    </span>
                    <span
                      className="text-xs tracking-wide"
                      style={{ color: "var(--color-text-light-green)" }}
                    >
                      {allQuestionsCorrect
                        ? "All questions answered correctly."
                        : "All questions resolved — see your TKSA score below."}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-md px-3.5 py-1.5 shrink-0">
                  {/* TKSA score chart — only renders when the box actually has
                  TKSA metadata attached to its questions. */}
                  {/* TKSA score — opens the detailed coverage modal. */}
                  {tksaScore.totalAll > 0 && (
                    <div className="relative mt-4 flex justify-start">
                      <button
                        type="button"
                        onClick={() => setTksaModalOpen(true)}
                        className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-all hover:shadow-[0_0_15px_rgba(0,255,0,0.3)]"
                        style={{
                          border: "1px solid var(--color-border-medium)",
                          background: "var(--color-primary-50)",
                          color: "var(--color-primary)",
                        }}
                      >
                        <BarChart3 className="w-4 h-4" />
                        See TKSA Score
                        <span
                          className="ml-1 font-mono tabular-nums"
                          style={{ color: "var(--color-text-light-green)" }}
                        >
                          {tksaScore.totalEarned}/{tksaScore.totalAll}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {mappingsModalOpen &&
            !isLoadingScenario &&
            hasScenarioResponse &&
            (hasTksaData || hasMitreData) && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                  onClick={() => setMappingsModalOpen(false)}
                />
                <div className="relative z-10 w-full max-w-2xl h-[90vh] flex flex-col overflow-hidden rounded-md border border-(--color-primary)/30 bg-(--color-bg-card) shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-(--color-primary)" />
                      <span className="text-sm font-semibold text-(--color-text-primary)">
                        TKSA Alignment and MITRE Mapping
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMappingsModalOpen(false)}
                      aria-label="Close"
                      className="rounded-md p-1.5 text-(--color-text-muted) hover:bg-white/5 hover:text-(--color-primary) transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Body (scrolls) */}
                  <div className="flex-1 overflow-y-auto p-5">
                    <div className="bg-transparent">
                      {/* Tab header */}
                      <div className="flex flex-wrap border-b border-(--color-primary)/15">
                        {hasTksaData && (
                          <button
                            type="button"
                            onClick={() => setFrameworkTab("tksa")}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                              activeFrameworkTab === "tksa"
                                ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                            }`}
                          >
                            <BarChart3 className="w-4 h-4" />
                            TKSA Alignment
                            <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                              {tksaBuckets.reduce(
                                (s, b) => s + b.items.length,
                                0,
                              )}
                            </span>
                          </button>
                        )}
                        {hasMitreData && (
                          <button
                            type="button"
                            onClick={() => setFrameworkTab("mitre")}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                              activeFrameworkTab === "mitre"
                                ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                            }`}
                          >
                            <ShieldAlert className="w-4 h-4" />
                            MITRE Mapping
                            <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                              {Object.values(mitreAttack)
                                .filter(Array.isArray)
                                .reduce((s, a) => s + a.length, 0)}
                            </span>
                          </button>
                        )}
                      </div>

                      {/* TKSA content */}
                      {activeFrameworkTab === "tksa" && hasTksaData && (
                        <div className="px-4 pb-4 pt-4 flex flex-col gap-4">
                          {tksaBuckets.map((bucket) => {
                            const colorMap = {
                              Tasks: {
                                dot: "#378ADD",
                                bg: "rgba(55,138,221,0.15)",
                                border: "rgba(55,138,221,0.3)",
                                text: "#85B7EB",
                              },
                              Skills: {
                                dot: "#639922",
                                bg: "rgba(99,153,34,0.15)",
                                border: "rgba(99,153,34,0.3)",
                                text: "#97C459",
                              },
                              Abilities: {
                                dot: "#7F77DD",
                                bg: "rgba(127,119,221,0.15)",
                                border: "rgba(127,119,221,0.3)",
                                text: "#AFA9EC",
                              },
                              Knowledge: {
                                dot: "#BA7517",
                                bg: "rgba(186,117,23,0.15)",
                                border: "rgba(186,117,23,0.3)",
                                text: "#EF9F27",
                              },
                              Other: {
                                dot: "#888888",
                                bg: "rgba(128,128,128,0.15)",
                                border: "rgba(128,128,128,0.3)",
                                text: "#AAAAAA",
                              },
                            };
                            const c = colorMap[bucket.label] ?? {
                              dot: "#888",
                              bg: "rgba(128,128,128,0.15)",
                              border: "rgba(128,128,128,0.3)",
                              text: "#aaa",
                            };

                            return (
                              <div key={bucket.label}>
                                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-(--color-text-muted) mb-2 flex items-center gap-2">
                                  {bucket.label}
                                  <span className="font-mono tabular-nums">
                                    {bucket.items.length}
                                  </span>
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {bucket.items.map((item) => (
                                    <span
                                      key={item}
                                      className="inline-flex items-center gap-1.5 text-xs font-mono font-medium px-2.5 py-1 rounded-md border"
                                      style={{
                                        background: c.bg,
                                        color: c.text,
                                        borderColor: c.border,
                                      }}
                                    >
                                      <span
                                        className="w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ background: c.dot }}
                                      />
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* MITRE content — flat backend list grouped into the ATT&CK
                          tactic → technique → sub-technique hierarchy on the frontend. */}
                      {activeFrameworkTab === "mitre" && hasMitreData && (
                        <MitreMappingTree mitre={mitreAttack} />
                      )}
                    </div>
                  </div>

                  {/* Footer — pinned to the bottom, MITRE tab only */}
                  {activeFrameworkTab === "mitre" && hasMitreData && (
                    <div className="shrink-0 border-t border-(--color-border) px-5 py-2.5 flex flex-wrap justify-end gap-4 text-[11px] text-(--color-text-muted)">
                      <span className="inline-flex items-center gap-1.5">
                        <CornerDownRight className="w-3 h-3" /> sub-technique
                        under its parent
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <ExternalLink className="w-3 h-3" /> IDs link to
                        attack.mitre.org
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>

        {/* Tabbed Interface */}
        <div className="bg-transparent border border-(--color-primary)/30 rounded-md overflow-hidden">
          {/* Tabs Header */}
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-2 sm:px-3 md:px-4 py-2.5 sm:py-3 md:py-4 flex items-center justify-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs md:text-sm leading-tight font-semibold transition-all relative ${
                    activeTab === tab.id
                      ? "text-(--color-primary) border-b-4 border-(--color-primary) shadow-[0_4px_15px_-2px_rgba(0,255,0,0)]"
                      : "bg-transparent text-(--color-text-muted) dark:text-gray-400 hover:bg-(--color-primary)/10 hover:text-(--color-text-primary) dark:hover:text-white border-b-2 border-transparent"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 shrink-0" />
                  <span className="hidden sm:inline whitespace-nowrap">
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-6 min-h-[50vh]">
            {activeTab === "play" && (
              <div className="space-y-6">
                {/* Main Container with Border */}
                <div className="border border-(--color-primary)/30 rounded-md p-3 sm:p-6 space-y-6 bg-transparent">
                  {/* Machine Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-(--color-primary)/10 rounded-md shrink-0">
                        <Monitor className="h-5 w-5 text-(--color-primary)" />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-(--color-text-primary)">
                            Machine Status
                          </p>
                          <span
                            className={`px-3 py-1 rounded-md text-xs font-semibold ${
                              machineStatus === "running"
                                ? "bg-(--color-primary)/20 border border-(--color-primary)/40 text-(--color-primary)"
                                : "bg-yellow-500/20 border border-yellow-500/40 text-yellow-400"
                            }`}
                          >
                            {machineStatus === "provisioning"
                              ? "Provisioning..."
                              : machineStatus === "running"
                                ? "Running"
                                : "Stopped"}
                          </span>
                          {machineStatus === "running" &&
                            (isMultiVm ? targetSubnet : privateIp) && (
                              <span className="px-3 py-1 text-xs text-(--color-text-muted) dark:text-gray-400 font-mono">
                                <b>
                                  {isMultiVm ? "TARGET_NETWORK" : "SCENARIO_IP"}
                                </b>{" "}
                                {isMultiVm ? targetSubnet : privateIp}
                              </span>
                            )}
                        </div>
                        <p className="text-xs text-(--color-text-muted) dark:text-gray-400 mt-1">
                          Ready to connect - Scenario IP Address will be
                          assigned after joining the machine
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:shrink-0">
                      <button
                        onClick={
                          canOpenAttackBox
                            ? handleOpenAttackBox
                            : handleStartAttackBox
                        }
                        disabled={
                          attackBoxBusy ||
                          (canOpenAttackBox
                            ? false
                            : machineStatus !== "running")
                        }
                        className="w-full sm:w-auto px-4 py-2 bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) font-semibold rounded-md hover:bg-(--color-primary)/20 hover:shadow-[0_0_15px_rgba(0,255,0,0.3)] transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-500/10 disabled:border-gray-500/40 disabled:text-gray-500 disabled:hover:shadow-none disabled:hover:bg-gray-500/10"
                      >
                        {canOpenAttackBox ? (
                          <ExternalLink className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        {attackBoxButtonLabel}
                      </button>
                    </div>
                  </div>

                  {attackBoxInlineMessage && (
                    <p
                      className={`text-xs ${
                        attackBoxUiPhase === ATTACKBOX_UI_PHASE.ERROR
                          ? "text-red-400"
                          : "text-(--color-text-muted) dark:text-gray-400"
                      }`}
                    >
                      {attackBoxInlineMessage}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-3">
                    <button
                      onClick={handleJoinMachine}
                      disabled={
                        joinBusy || buttonMode !== "stopped" || isLoading
                      }
                      className="w-full sm:flex-1 px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 text-xs sm:text-sm md:text-base bg-(--color-primary) text-white dark:text-black font-semibold rounded-md hover:shadow-[0_0_20px_rgba(0,255,0,0.5)] transition-all flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-(--color-primary)/50 disabled:hover:shadow-none"
                    >
                      <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {joinBusy ? "Joining..." : "Join Machine"}
                    </button>

                    {/* Team Challenge button */}
                    {/* Team Challenge button */}
                    {isMultiVm ? (
                      canControlTeamScenario ? (
                        <button
                          disabled
                          title="Team Challenge — coming soon in beta"
                          className="group relative w-full sm:w-auto px-4 py-2 sm:px-5 sm:py-2.5 md:py-3 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center gap-2 cursor-not-allowed overflow-hidden border border-dashed border-(--color-border) bg-(--color-bg-card)/40 dark:bg-white/2 text-(--color-text-muted)"
                        >
                          <ShieldAlert className="h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-60 shrink-0" />
                          <span className="whitespace-nowrap">
                            Team Challenge
                          </span>
                          <span className="ml-0.5 inline-flex items-center gap-1 rounded-sm border border-(--color-primary)/30 bg-(--color-primary)/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-(--color-primary) whitespace-nowrap">
                            <Lock className="h-2.5 w-2.5" />
                            Beta
                          </span>
                        </button>
                      ) : null
                    ) : canControlTeamScenario ? (
                      <button
                        onClick={() => setShowTeamChallengeModal(true)}
                        disabled={isLoading}
                        className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 text-xs sm:text-sm md:text-base font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 sm:gap-2 bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) hover:bg-(--color-primary)/20 hover:shadow-[0_0_15px_rgba(0,255,0,0.3)] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-500/10 disabled:border-gray-500/40 disabled:text-gray-500"
                      >
                        <ShieldAlert className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        Team Challenge
                      </button>
                    ) : null}

                    <button
                      onClick={handleStopMachine}
                      disabled={
                        buttonMode !== "running" ||
                        isStoppingMachine ||
                        joinBusy ||
                        attackBoxBusy ||
                        machineStatus !== "running"
                      }
                      className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 text-xs sm:text-sm md:text-base bg-transparent border border-red-500/50 text-red-400 font-semibold rounded-md hover:bg-red-500/10 transition-all flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-500/50 disabled:text-gray-500 disabled:hover:bg-transparent"
                    >
                      <Square className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {isStoppingMachine ? "Stopping..." : "Stop Machine"}
                    </button>

                    {/* VPN button */}
                    {isMultiVm ? (
                      <button
                        disabled
                        title="Connect using VPN — coming soon in beta"
                        className="group relative w-full sm:w-auto px-4 py-2 sm:px-5 sm:py-2.5 md:py-3 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center gap-2 cursor-not-allowed overflow-hidden border border-dashed border-(--color-border) bg-(--color-bg-card)/40 dark:bg-white/2 text-(--color-text-muted)"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-60 shrink-0" />
                        <span className="whitespace-nowrap">
                          Connect using VPN
                        </span>
                        <span className="ml-0.5 inline-flex items-center gap-1 rounded-sm border border-(--color-primary)/30 bg-(--color-primary)/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-(--color-primary) whitespace-nowrap">
                          <Lock className="h-2.5 w-2.5" />
                          Beta
                        </span>
                      </button>
                    ) : (
                      <button
                        onClick={handleDownloadVpn}
                        disabled={
                          buttonMode !== "running" ||
                          isDownloadingVpn ||
                          machineStatus !== "running"
                        }
                        className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 text-xs sm:text-sm md:text-base bg-(--color-bg-card)/80 dark:bg-black/30 backdrop-blur-sm border border-(--color-border) text-(--color-text-primary) font-semibold rounded-md hover:border-(--color-primary) transition-all flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-900/30 disabled:border-gray-600/40 disabled:text-gray-600 disabled:hover:border-gray-600/40"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        {isDownloadingVpn
                          ? "Downloading..."
                          : "Connect using VPN"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Connection Warning */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-md flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-400">
                      Connection Required
                    </p>
                    <p className="text-xs text-red-400 mt-1">
                      Click "Join Machine" to spawn the virtual machine and
                      receive connection details. VPN configuration may be
                      required.
                    </p>
                  </div>
                </div>

                {/* Questions */}
                <LabQuestionsSection
                  showQuestionsLoader={showQuestionsLoader}
                  totalQuestions={totalQuestions}
                  questions={questions}
                  answers={answers}
                  submitted={submitted}
                  attempts={attempts}
                  setAnswers={setAnswers}
                  setSubmitted={setSubmitted}
                  handleSubmitAnswer={handleSubmitAnswer}
                  revealedAnswers={revealedAnswers}
                  onAttemptQuestion={(idx) => {
                    setAttemptedQuestions((prev) =>
                      prev[idx] ? prev : { ...prev, [idx]: true },
                    );
                  }}
                  missionTitle={scenarioDetails?.title || safeLab?.title}
                  missionDescription={
                    scenarioDetails?.description || safeLab?.description
                  }
                  difficulty={
                    safeLab?.difficulty || scenarioDetails?.difficulty
                  }
                />
              </div>
            )}

            {activeTab === "info" && (
              <div>
                {isLoading || isLoadingScenario ? (
                  <div className="space-y-4 p-2">
                    <div className="h-8 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-72"></div>
                    <div className="h-5 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full max-w-xl"></div>
                    <div className="h-5 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-2/3"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-24 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%]"
                        ></div>
                      ))}
                    </div>
                  </div>
                ) : (isCompanyAdmin ||
                    isPlatformAdmin ||
                    isProfessional ||
                    (isCompanyUser && canRequestScenario)) &&
                  safeLab &&
                  parentCategory ? (
                  isMultiVm ? (
                    /* Multi-VM: Request a Box locked — matches the empty-state style of the
     Walkthroughs / Response & Remediation tabs. */
                    <div className="text-center py-12 text-(--color-text-muted) dark:text-gray-400">
                      <Lock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-base font-semibold text-(--color-text-primary) mb-1">
                        Beta Coming Soon
                      </p>
                      <p className="text-sm">
                        Custom box requests for multi-VM labs are coming in a
                        future update.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Hero Section */}
                      <div className="relative overflow-hidden rounded-md border border-(--color-primary)/30 bg-linear-to-br from-(--color-primary)/5 via-transparent to-(--color-primary)/10 p-8">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-(--color-primary)/10 rounded-md blur-3xl -mr-32 -mt-32"></div>
                        <div className="relative z-10 space-y-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-1 space-y-3">
                              <h3 className="text-2xl font-bold text-(--color-text-primary) [text-shadow:0_0_15px_var(--color-shadow-primary)]/20">
                                Request a Custom Box
                              </h3>
                              <p className="text-base text-(--color-text-muted) dark:text-gray-400 max-w-3xl leading-relaxed">
                                Need a specific vulnerability covered? We'll
                                create a tailored box environment designed
                                around your requirements.
                              </p>
                            </div>
                          </div>

                          {/* Features Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                            <div className="flex items-start gap-3 p-4 rounded-md bg-(--color-bg-card)/80 dark:bg-black/25 backdrop-blur-sm border border-(--color-primary)/20">
                              <div className="shrink-0 mt-1">
                                <svg
                                  className="h-5 w-5 text-(--color-primary)"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-(--color-text-primary) mb-1">
                                  Customized Content
                                </h4>
                                <p className="text-xs text-(--color-text-muted) dark:text-gray-400">
                                  Boxes designed specifically for your chosen
                                  vulnerability
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start gap-3 p-4 rounded-md bg-(--color-bg-card)/80 dark:bg-black/25 backdrop-blur-sm border border-(--color-primary)/20">
                              <div className="shrink-0 mt-1">
                                <svg
                                  className="h-5 w-5 text-(--color-primary)"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 10V3L4 14h7v7l9-11h-7z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-(--color-text-primary) mb-1">
                                  Fast Delivery
                                </h4>
                                <p className="text-xs text-(--color-text-muted) dark:text-gray-400">
                                  New boxes generated and deployed quickly
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start gap-3 p-4 rounded-md bg-(--color-bg-card)/80 dark:bg-black/25 backdrop-blur-sm border border-(--color-primary)/20">
                              <div className="shrink-0 mt-1">
                                <svg
                                  className="h-5 w-5 text-(--color-primary)"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                  />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-(--color-text-primary) mb-1">
                                  Complete Walkthroughs
                                </h4>
                                <p className="text-xs text-(--color-text-muted) dark:text-gray-400">
                                  Detailed guides and solutions included
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* CTA Buttons */}
                          <div className="pt-4 flex flex-wrap justify-start gap-3">
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-md border-2 border-(--color-primary) bg-(--color-primary) px-6 py-3.5 text-base font-medium text-(--color-text-on-primary) transition-all hover:shadow-[0_0_15px_rgba(0,255,0,0.5)]"
                              onClick={() => setShowTopologyModal(true)}
                            >
                              <Network className="h-5 w-5" />
                              Request Box Revision (Topology)
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-md border-2 border-(--color-primary) bg-(--color-primary) px-6 py-3.5 text-base font-medium text-(--color-text-on-primary) transition-all hover:shadow-[0_0_15px_rgba(0,255,0,0.5)]"
                              onClick={() => setShowDescriptionModal(true)}
                            >
                              <FileText className="h-5 w-5" />
                              Request Box Revision (Description)
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* How It Works */}
                      <div className="rounded-md border border-(--color-primary)/20 bg-transparentbackdrop-blur-sm p-6">
                        <h4 className="text-lg font-bold text-(--color-text-primary) mb-4 flex items-center gap-2">
                          How It Works
                        </h4>
                        <div className="grid gap-4 sm:grid-cols-3">
                          {[
                            {
                              step: "1",
                              title: "Submit Request",
                              desc: "Describe the vulnerability or CVE you want to practice",
                            },
                            {
                              step: "2",
                              title: "We Build It",
                              desc: "Our team creates a custom box environment for you",
                            },
                            {
                              step: "3",
                              title: "Start Learning",
                              desc: "Access your new box and begin hands-on practice",
                            },
                          ].map(({ step, title, desc }) => (
                            <div key={step} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-(--color-primary) text-xs font-bold text-(--color-text-on-primary)">
                                  {step}
                                </span>
                                <h5 className="font-semibold text-(--color-text-primary)">
                                  {title}
                                </h5>
                              </div>
                              <p className="text-sm text-(--color-text-muted) dark:text-gray-400 pl-9">
                                {desc}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                ) : isCompanyUser && !canRequestScenario ? (
                  <div className="text-center py-16 px-4">
                    <div className="inline-flex h-20 w-20 items-center justify-center rounded-md border-2 border-(--color-primary)/30 bg-(--color-primary)/10 mb-4">
                      <ShieldAlert className="h-10 w-10 text-(--color-primary)/60" />
                    </div>
                    <h3 className="text-xl font-bold text-(--color-text-primary) mb-2">
                      You do not have permission to request boxes
                    </h3>
                    <p className="text-sm text-(--color-text-muted) dark:text-gray-400 max-w-md mx-auto">
                      Contact your administrator to enable box creation access.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-16 px-4">
                    <div className="inline-flex h-20 w-20 items-center justify-center rounded-md border-2 border-(--color-primary)/30 bg-(--color-primary)/10 mb-4">
                      <ShieldAlert className="h-10 w-10 text-(--color-primary)/60" />
                    </div>
                    <h3 className="text-xl font-bold text-(--color-text-primary) mb-2">
                      Professional Feature
                    </h3>
                    <p className="text-sm text-(--color-text-muted) dark:text-gray-400 max-w-md mx-auto">
                      This is a professional feature you cannot access.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "walkthroughs" && (
              <LabWalkthroughTab
                isLoadingScenario={
                  isLoadingScenario || isLoading || !hasScenarioResponse
                }
                isLocked={!allQuestionsAttempted}
                walkthroughMarkdown={walkthroughMarkdown}
                handleDownloadPDF={handleDownloadPDF}
                walkthroughRef={walkthroughRef}
              />
            )}

            {activeTab === "remediation" && (
              <LabRemediationTab
                isLoadingScenario={
                  isLoadingScenario || isLoading || !hasScenarioResponse
                }
                isLocked={!allQuestionsAttempted}
                playbookMarkdown={playbookMarkdown}
                runbookMarkdown={runbookMarkdown}
                title={scenarioDetails?.title || safeLab?.title}
              />
            )}

            {activeTab === "topology" && (
              <LabTopologyTab
                scenarioId={lab?.id || labId}
                nodes={labTopologyNodes}
                edges={labTopologyEdges}
              />
            )}
          </div>
        </div>
      </div>
      {showAttackBoxLaunchModal && (
        <AttackBoxLaunchModal
          url={attackBoxUrl}
          onClose={() => setShowAttackBoxLaunchModal(false)}
          onOpen={handleLaunchAttackBoxUrl}
          isMultiVm={isMultiVm}
        />
      )}
      <TksaCoverageModal
        open={tksaModalOpen}
        onClose={() => setTksaModalOpen(false)}
        buckets={tksaCoverageDetail.buckets}
        totalEarned={tksaCoverageDetail.totalEarned}
        totalAll={tksaCoverageDetail.totalAll}
        tksaEntries={tksaCoverageDetail.tksaEntries}
        questionsCorrect={questionsCorrectCount}
        questionsResolved={questionsResolvedCount}
        totalQuestions={totalQuestions}
      />
      {isPlatformAdmin ? (
        <PlatformTeamChallengeModal
          open={showTeamChallengeModal}
          onClose={() => setShowTeamChallengeModal(false)}
          scenarioId={lab?.id || labId}
        />
      ) : (
        <TeamChallengeModal
          open={showTeamChallengeModal}
          onClose={() => setShowTeamChallengeModal(false)}
          scenarioId={lab?.id || labId}
        />
      )}

      {/* Floating Chat Bubble */}
      <ChatWidget scenarioId={chatScenarioId} />
    </PageLayout>
  );
}
